import classNames from 'classnames'
import React from 'react'
import { useTranslations } from '../../hooks/useTranslations'
import { InternalExternalLink } from '../../types/InternalExternalLink'
import { notEmpty } from '../../utilities/filters'
import { isNullOrWhiteSpace } from '../../utilities/string'
import { Box } from '../Box'
import { Button } from '../Button'
import { Grid } from '../Grid'
import { Image, ImageProps } from '../Image'
import { Paragraph } from '../Paragraph'
import { TableList, TableListRow } from '../TableList/TableList'

export interface TextWithTableProps {
  button?: InternalExternalLink
  heading?: string
  headingAs?: keyof JSX.IntrinsicElements
  /**
   * Image to show to the right instead of the table
   * when there's no table to show.
   */
  image?: ImageProps
  links?: InternalExternalLink[]
  linksText?: string
  preamble?: string
  subtitle?: string
  subtitleAs?: keyof JSX.IntrinsicElements
}

export const TextWithTable = ({
  button,
  heading,
  headingAs: Heading = 'h1',
  image,
  links,
  linksText,
  preamble,
  subtitle,
  subtitleAs: Subtitle = 'h2',
}: TextWithTableProps) => {
  const translations = useTranslations()

  const rows =
    links?.filter(notEmpty).map<TableListRow>(item => ({
      columns: [
        {
          isExternalLink: item.isExternal,
          link: item.url,
          text: item.title,
        },
      ],
    })) ?? []

  const renderBox = !isNullOrWhiteSpace(linksText) || !!links?.length
  const renderImage = !isNullOrWhiteSpace(image?.src) && !renderBox

  return (
    <Grid>
      <div
        className={classNames(
          'col-span-full max-w-330 mx-auto',
          'tb:max-w-full tb:col-start-1 tb:col-span-2 tb:pl-30 tb:mx-0',
          'sm:col-start-2 sm:col-span-2 sm:pl-0',
          'md:col-start-1 md:col-span-3',
          'lg:col-start-2 lg:col-span-3',
          'xxl:col-start-3 xxl:col-span-3'
        )}
      >
        {!isNullOrWhiteSpace(heading) && (
          <Heading
            className={classNames(
              'mb-15 font-h2 text-h2 text-center text-secondary-dark',
              'tb:text-left'
            )}
          >
            {heading}
          </Heading>
        )}

        {!isNullOrWhiteSpace(subtitle) && (
          <Subtitle
            className={classNames(
              'mb-30 text-center font-h4 text-h4 text-secondary-darker',
              'tb:text-left',
              'md:pr-60',
              'lg:mb-30',
              'xxl:mb-45'
            )}
          >
            {subtitle}
          </Subtitle>
        )}

        {!isNullOrWhiteSpace(preamble) && (
          <Paragraph
            className={classNames(
              'hidden text-secondary-darkest font-body text-body',
              'tb:pr-5 tb:text-left tb:block',
              'sm:pl-0 sm:pr-15',
              'md:ml-0 md:pr-90',
              'lg:max-w-480 lg:pr-0',
              'xl:max-w-[31.875rem]',
              'xxl:pr-30'
            )}
          >
            {preamble}
          </Paragraph>
        )}

        {button && !isNullOrWhiteSpace(button.url) && (
          <div className="hidden tb:mt-45 tb:block">
            <Button
              additionalClassName="mt-auto self-start"
              isExternalUrl={button.isExternal}
              url={button.url}
            >
              {!isNullOrWhiteSpace(button.title) ? button.title : translations.more}
            </Button>
          </div>
        )}
      </div>

      {renderBox && (
        <Box
          additionalClassName={classNames(
            'col-span-full space-y-30',
            'tb:col-start-3 tb:col-span-2 tb:self-start tb:mr-30',
            'sm:col-start-4 sm:col-span-2',
            'lg:col-start-6 lg:col-span-2',
            'xl:col-start-7 xl:col-span-2',
            'xxl:col-start-9 xxl:col-span-3'
          )}
          padding="p-45 xxl:p-60"
          paddingStyle="custom"
          variant={'border'}
        >
          {!isNullOrWhiteSpace(linksText) && (
            <Paragraph className="font-t-light text-t-300 text-secondary-dark xl:text-t-400">
              {linksText}
            </Paragraph>
          )}

          {!!rows.length && (
            <TableList
              linkTextColor="text-secondary"
              listItemTextSize="font-t text-t-300 xl:text-t-400 xxl:text-t-500"
              rowGapY="py-8 xxl:py-15"
              rows={rows}
              topBorder={false}
            />
          )}
        </Box>
      )}

      {renderImage && image && (
        <div
          className={classNames(
            'col-span-full space-y-30',
            'tb:flex tb:flex-col tb:justify-center tb:h-full',
            'tb:col-start-3 tb:col-span-2 tb:self-start tb:mr-30',
            'sm:col-start-4 sm:col-span-2',
            'lg:col-start-5 lg:col-span-3',
            'xl:col-start-6 xl:col-span-3',
            'xxl:col-start-8 xxl:col-span-4'
          )}
        >
          <Image {...image} caption="" copyright="" />
        </div>
      )}

      {button && !isNullOrWhiteSpace(button.url) && (
        <div className="mt-45 flex justify-center col-span-full tb:hidden">
          <Button
            additionalClassName="mt-auto self-start"
            isExternalUrl={button.isExternal}
            url={button.url}
          >
            {!isNullOrWhiteSpace(button.title) ? button.title : translations.more}
          </Button>
        </div>
      )}
    </Grid>
  )
}
